import React, { useContext, useState } from "react";
import { BACKEND_URL } from "../../../config/config";
import axios from "axios";
// import { Button } from "../../misc/button/Button";
import { Alert, Card, Divider, Space, Upload, Spin, Typography, Progress, Button } from "antd";
import { UploadOutlined, InfoCircleOutlined, DownloadOutlined } from "@ant-design/icons";
import { NotificationContext } from "../../context/NotificationWrapper";

const { Title, Paragraph, Text } = Typography;

function PaymentStatusChangePage() {
  const { openNotification } = useContext(NotificationContext);
  const [loading, setLoading] = useState(false);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [uploadResult, setUploadResult] = useState(null);
  const [reportId, setReportId] = useState(null);

  const handleUpload = async (options) => {
    const { file, onSuccess, onError } = options;
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      setLoading(true);
      setUploadErrors([]);
      setUploadResult(null);
      setReportId(null);
      
      const response = await axios.post(
        `${BACKEND_URL}/api/payment/status-change`,
        formData,
        {
          withCredentials: true,
          timeout: 600000, // 10 minutes (600 000 ms)
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      
      onSuccess("OK");
      
      // Traiter la réponse au nouveau format
      setUploadResult({
        processed: response.data.processed,
        total: response.data.total
      });
      
      if (response.data.errors && response.data.errors.length > 0) {
        setUploadErrors(response.data.errors);
      }

      if (response.data.reportId) {
        setReportId(response.data.reportId);
      }
      
      openNotification(
        "Succès",
        `Le fichier a été traité : ${response.data.processed} lignes sur ${response.data.total} traitées`,
        "success"
      );
    } catch (error) {
      onError("Erreur d'upload");
      
      if (error.response && error.response.data) {
        if (typeof error.response.data === 'string') {
          setUploadErrors([error.response.data]);
        } else if (error.response.data.message) {
          setUploadErrors([error.response.data.message]);
        } else {
          setUploadErrors(["Une erreur est survenue lors du traitement du fichier"]);
        }
      } else {
        openNotification(
          "Erreur",
          "Une erreur est survenue lors du traitement du fichier",
          "error"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BACKEND_URL}/api/payment/export-template`,
        {
          withCredentials: true,
          responseType: "blob",
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "modele_statut_paiements.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      openNotification(
        "Erreur",
        "Impossible de télécharger le modèle de fichier",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = async () => {
    if (!reportId) {
      openNotification(
        "Information",
        "Aucun rapport disponible pour ce traitement",
        "info"
      );
      return;
    }
    
    try {
      setLoading(true);
      const response = await axios.get(
        `${BACKEND_URL}/api/payment/download-import-report/${reportId}`,
        {
          withCredentials: true,
          responseType: "blob",
        }
      );
      
      // Le backend renvoie toujours un CSV pour le rapport
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      
      // Extraire le nom du fichier de l'en-tête Content-Disposition si disponible
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'rapport_import.csv';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=["']?([^"';\n]*)["']?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      openNotification(
        "Succès",
        "Rapport téléchargé avec succès",
        "success"
      );
    } catch (error) {
      console.error("Erreur lors du téléchargement du rapport:", error);
      
      // Gérer les cas d'erreur HTTP (rapport non trouvé, etc.)
      let errorMessage = "Impossible de télécharger le rapport d'importation";
      
      if (error.response) {
        if (error.response.status === 404) {
          errorMessage = "Le rapport n'existe plus ou a été supprimé";
        } else if (error.response.status === 400) {
          errorMessage = "Format de rapport invalide";
        }
      }
      
      openNotification(
        "Erreur",
        errorMessage,
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="block-right">
      <Spin spinning={loading} tip="Traitement en cours...">
        <Card className="payment-status-card">
          <Title level={2}>Gestion des statuts de paiement</Title>
          
          <Alert
            type="info"
            icon={<InfoCircleOutlined />}
            showIcon
            message="Guide d'utilisation"
            description={
              <div>
                <Paragraph>
                  Cette page vous permet de mettre à jour en masse les statuts de paiement des déclarations via un fichier CSV.
                </Paragraph>
                <Paragraph>
                  Le fichier doit respecter un format précis avec les colonnes suivantes :
                </Paragraph>
                <ul>
                  <li><Text strong>Identifiant de la déclaration</Text> - L'identifiant unique de la déclaration</li>
                  <li><Text strong>Statut de paiement</Text> - Le nouveau statut à appliquer ("yes", "no" ou "ongoing")</li>
                </ul>
                <Paragraph>
                  Le système traitera toutes les lignes du fichier et mettra à jour les statuts de paiement correspondants.
                </Paragraph>
                <Paragraph>
                  Toutes les déclarations traitées seront passées en <strong>virement</strong>.<br />
                   Il n'est pas possible d'attribuer le statut <strong>CB</strong>.<br />
                  Les paiements par CB et en statut <strong>yes</strong> ne peuvent pas être modifiés.
                </Paragraph>
              </div>
            }
            style={{ marginBottom: 24 }}
          />

          {uploadErrors.length > 0 && (
            <Alert
              type="error"
              message="Erreurs lors du traitement du fichier"
              description={
                <ul>
                  {uploadErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              }
              showIcon
              closable
              style={{ marginBottom: 24 }}
              action={
                <Button 
                  size="small" 
                  type="primary"
                  onClick={handleDownloadReport}
                >
                  Télécharger le rapport
                </Button>
              }
            />
          )}

          {uploadResult && (
            <div style={{ marginBottom: 24 }}>
              <Title level={4}>Résultat du traitement</Title>
              <div style={{ marginBottom: 16 }}>
                <Progress 
                  percent={uploadResult.total ? Math.round((uploadResult.processed / uploadResult.total) * 100) : 0} 
                  status={uploadErrors.length > 0 ? "exception" : "success"}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text>Lignes traitées: <Text strong>{uploadResult.processed}</Text> sur <Text strong>{uploadResult.total}</Text></Text>
                <Text>Statut: <Text strong type={uploadErrors.length > 0 ? "danger" : "success"}>
                  {uploadErrors.length > 0 ? "Terminé avec erreurs" : "Terminé avec succès"}
                </Text></Text>
              </div>
            </div>
          )}

          <Space direction="vertical" style={{ width: '100%' }}>
            <Divider orientation="left">Importer un fichier CSV</Divider>
            <Space size="middle">
              <Upload
                name="file"
                customRequest={handleUpload}
                showUploadList={true}
                accept=".csv"
                maxCount={1}
              >
                <Button icon={<UploadOutlined />} type="primary">
                  Sélectionner un fichier CSV
                </Button>
              </Upload>
              <Button 
                  icon={<DownloadOutlined />} 
                  onClick={handleDownloadTemplate}
                >
                  Télécharger le modèle de fichier
              </Button>
              {reportId && (
                <Button 
                  icon={<DownloadOutlined />} 
                  onClick={handleDownloadReport}
                >
                  Télécharger le rapport
                </Button>
              )}
            </Space>
          </Space>
        </Card>
      </Spin>
    </div>
  );
}

export default PaymentStatusChangePage;